import Draw, { createBox } from 'ol/interaction/Draw.js';
import { Vector as VectorLayer } from 'ol/layer.js';
import { Vector as VectorSource } from 'ol/source.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import Polygon from 'ol/geom/Polygon.js';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style.js';
import * as turf from '@turf/turf';


// El punto de origen se perdía sobre el mapa: el estilo por defecto de OpenLayers
// es un círculo de 5 px del mismo celeste que el resto de las geometrías. Se le
// da un marcador propio, con halo para que se lea sobre cualquier mapa base.
//
// Esta capa la comparten los cuatro geoprocesos, así que todo lo que no sea un
// punto conserva exactamente el estilo por defecto de OpenLayers: sólo cambia
// cómo se ve el punto.
const DEFAULT_FILL = new Fill({ color: 'rgba(255, 255, 255, 0.4)' });
const DEFAULT_STROKE = new Stroke({ color: '#3399CC', width: 1.25 });

const DEFAULT_STYLE = new Style({
  fill: DEFAULT_FILL,
  stroke: DEFAULT_STROKE,
  image: new CircleStyle({ radius: 5, fill: DEFAULT_FILL, stroke: DEFAULT_STROKE }),
});

const POINT_STYLE = [
  // halo blanco, para que el marcador no se pierda sobre fondos oscuros
  new Style({
    image: new CircleStyle({
      radius: 9,
      fill: new Fill({ color: 'rgba(255, 255, 255, 0.9)' }),
    }),
  }),
  new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: '#e8730c' }),
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),
];

// La línea se conserva sobre sus áreas de influencia para que el usuario
// pueda distinguir el trazado original incluso cuando existen varios buffers.
const LINE_STYLE = new Style({
  stroke: new Stroke({ color: '#e8730c', width: 2.5 }),
});

// Compartido por los buffers de la lineal y los anillos de la radial: con el
// estilo por defecto (blanco 40% y borde de 1.25 px) los anillos se perdían
// sobre el mapa base y la vista previa parecía no dibujarse.
const BUFFER_STYLE = new Style({
  fill: new Fill({ color: 'rgba(232, 115, 12, 0.12)' }),
  stroke: new Stroke({ color: 'rgba(232, 115, 12, 0.8)', width: 1.5 }),
});

function geoprocessingStyle(feature) {
  return feature.getGeometry().getType() === 'Point' ? POINT_STYLE : DEFAULT_STYLE;
}

// Las zonas de la pluma son concéntricas; con el estilo por defecto (mismo
// relleno y borde para todas) las zonas contiguas se leen como una sola. Se
// les da un color por zona (mayor a menor concentración) vía setStyle por
// feature, sin tocar el estilo por defecto que comparten los demás geoprocesos.
// Los rellenos se apilan en el centro (rojo sobre naranjo sobre amarillo),
// así que el interior necesita más opacidad y un tono más oscuro para no
// terminar pareciéndose a la zona intermedia.
const PLUME_ZONE_STYLES = [
  new Style({
    fill: new Fill({ color: 'rgba(165, 15, 21, 0.35)' }),
    stroke: new Stroke({ color: '#a50f15', width: 2.5 }),
  }),
  new Style({
    fill: new Fill({ color: 'rgba(230, 85, 13, 0.22)' }),
    stroke: new Stroke({ color: '#e6550d', width: 2 }),
  }),
  new Style({
    fill: new Fill({ color: 'rgba(254, 217, 118, 0.20)' }),
    stroke: new Stroke({ color: '#d9b310', width: 2 }),
  }),
];


let draw; // interacción activa
let source = new VectorSource({ wrapX: false });
let vector;
let coords;
let radius;
let center;

source.on('addfeature', function (evt) {
  const feature = evt.feature;
  const geom = feature.getGeometry();
  const geomType = geom.getType();

  if (geomType === 'Point') {
    center = geom.getCoordinates();
    console.log("🟢 Centro actualizado:", center);
  } else if (geomType === 'Circle') {
    center = geom.getCenter();
    radius = geom.getRadius();
    console.log("🟢 Centro (desde círculo):", center, "Radio:", radius);
  } else {
    coords = geom.getCoordinates();
  }
});

export const geoprocessingsInteraction = {
  data() {
    return {
      message: 'Hola',
      lastGeometry: null,
    };
  },
  methods: {
    addGeoInteraction(drawType) {
      // 🧠 drawType puede venir como { name, code }
      const typeCode = typeof drawType === 'string' ? drawType : drawType.code;
      // opcional: { once: true } desactiva la interacción al terminar el dibujo
      const once = typeof drawType === 'object' && drawType.once;

      // ✅ limpia interacciones previas
      if (draw) this.map.removeInteraction(draw);
      if (!vector) {
        // zIndex alto: la pluma y el punto de origen deben quedar siempre
        // sobre las capas aplicadas (que usan su "orden" como zIndex)
        vector = new VectorLayer({ source, style: geoprocessingStyle, zIndex: 1000 });
        this.map.addLayer(vector);
      }

      let geometryFunction = undefined;

      // 🟥 caso especial: "Box" (rectángulo)
      if (typeCode === 'Box') {
        geometryFunction = createBox();
      }

      // ✅ soporta Polygon, Box y LineString
      draw = new Draw({
        source: source,
        type: geometryFunction ? 'Circle' : typeCode, // "Circle" usado como base para Box
        geometryFunction: geometryFunction,
      });

      draw.on('drawstart', function () {
        source.clear(); // limpiar anterior
      });

      draw.on('drawend', this.onDrawEnd);

      // los geoprocesos que dibujan una sola geometría sacan la interacción al
      // terminar, si no el sketch queda siguiendo al cursor
      if (once) {
        draw.on('drawend', () => {
          this.map.removeInteraction(draw);
          draw = null;
        });
      }

      this.map.addInteraction(draw);

      console.log(`✏️ Iniciada interacción de dibujo: ${typeCode}`);
    },

    removeGeoInteraction() {
      if (vector) this.map.removeLayer(vector);
      if (draw) this.map.removeInteraction(draw);
      source.clear();
      draw = null;
      vector = null;
      coords = null;
      radius = null;
      center = null;
      this.lastGeometry = null;
    },

    // Quita solo la interacción de dibujo y conserva la geometría ya dibujada
    // (a diferencia de removeGeoInteraction, que borra capa y geometría).
    // Devuelve true si había algo que suspender.
    suspendGeoInteraction() {
      if (!draw) return false;
      this.map.removeInteraction(draw);
      draw = null;
      return true;
    },

    cleanGeoDraw() {
      source.clear();
      coords = null;
      this.lastGeometry = null;
    },

    getPolygonCoords() {
      return coords;
    },
    getCircleRadius() {
      return radius;
    },
    getCenter() {
      return center;
    },

    getLastGeometryGeoJSON() {
      if (!this.lastGeometry) return null;
      const format = new GeoJSON();
      return format.writeGeometryObject(this.lastGeometry, {
        featureProjection: this.map.getView().getProjection(),
        dataProjection: 'EPSG:3857',
      });
    },

    onDrawEnd(evt) {
      const geometry = evt.feature.getGeometry();
      this.lastGeometry = geometry;

      // ✅ Standardized Geometry Payload
      const type = geometry.getType();
      const coordinates = geometry.getCoordinates();

      const inputGeometries = [{
        type: type,
        coordinates: coordinates
      }];

      const customGeometry = {
        input_geometries: inputGeometries
      };

      console.log("✅ Geometría capturada (Standardized):", customGeometry);

      // ✅ FIX: Asignar directamente a la variable del componente
      this.geoRequestData = customGeometry;

      this.executeMapToolAction('geoprocessing', 'ready', {
        geoRequestData: customGeometry,
      });
    },

    /**
     * Dibuja los anillos de influencia radial
     * @param {Array} distances - Array de distancias en metros
     */
    drawRings(distances) {
      if (!center) {
        console.warn("⚠️ No hay un punto central definido para dibujar anillos.");
        return;
      }

      console.log("✏️ Dibujando anillos para distancias (m):", distances);
      source.clear();

      // 1. Re-agregar el punto central
      const pointFeature = new Feature(new Point(center));
      source.addFeature(pointFeature);

      const inputGeometries = [];

      // 2. Agregar círculos
      // El radio se interpreta en metros REALES. Un círculo plano en Web
      // Mercator mide radio*cos(lat) metros en el suelo: "50 km" eran ~41,7 km
      // en Santiago y ~30 km en Magallanes. Igual que drawLineBuffers, el
      // buffer se calcula en EPSG:4326 con Turf y vuelve a la proyección del
      // mapa, así el anillo dibujado y la geometría analizada son la misma.
      const format = new GeoJSON();
      const mapProjection = this.map.getView().getProjection();
      const pointGeoJSON = format.writeFeatureObject(pointFeature, {
        dataProjection: 'EPSG:4326',
        featureProjection: mapProjection,
      });

      let largestRing = null;

      distances.forEach((distM) => {
        const radiusMeters = distM;

        const bufferedGeoJSON = turf.buffer(pointGeoJSON, radiusMeters, { units: 'meters', steps: 64 });
        if (!bufferedGeoJSON) {
          console.warn(`⚠️ No fue posible generar el anillo de ${radiusMeters} m.`);
          return;
        }

        const circleFeature = format.readFeature(bufferedGeoJSON, {
          dataProjection: 'EPSG:4326',
          featureProjection: mapProjection,
        });
        circleFeature.setStyle(BUFFER_STYLE);
        source.addFeature(circleFeature);
        // Las distancias son crecientes: el último anillo agregado es el mayor.
        largestRing = circleFeature.getGeometry();

        // Standardized payload data for Radial
        inputGeometries.push({
          type: "Polygon",
          coordinates: circleFeature.getGeometry().getCoordinates(),
          radius: radiusMeters // Optional, valid for Radial
        });
      });

      // A escala regional un radio de cientos de metros mide pocos píxeles y
      // queda oculto bajo el marcador del punto: ajustar la vista al anillo
      // mayor hace visible la previsualización sin importar el zoom actual.
      if (largestRing) {
        this.map.getView().fit(largestRing.getExtent(), {
          padding: [60, 60, 60, 60],
          maxZoom: 16,
          duration: 300,
        });
      }

      // Construct the standardized custom geometry object
      const customGeometry = {
        input_geometries: inputGeometries,
        // type can be inferred from context or sent separately. 
        // Here we just prepare data structure.
      };

      console.log("📦 [DEBUG] Custom Geometry Payload (Standardized):", customGeometry);

      // ✅ FIX: Asignar directamente
      this.geoRequestData = customGeometry;

      this.executeMapToolAction("geoprocessing", "ready", {
        geoRequestData: customGeometry,
      });
    },

    /**
     * Genera y dibuja las áreas de influencia de la última polilínea.
     * Turf calcula el buffer en EPSG:4326 para interpretar correctamente los
     * metros; luego las geometrías vuelven a la proyección del mapa.
     *
     * @param {Array} distances - Distancias de influencia en metros
     */
    drawLineBuffers(distances) {
      if (!this.lastGeometry || this.lastGeometry.getType() !== 'LineString') {
        console.warn("⚠️ No hay una polilínea definida para generar el buffer.");
        return;
      }

      const validDistances = distances
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (validDistances.length === 0) {
        return;
      }

      const format = new GeoJSON();
      const mapProjection = this.map.getView().getProjection();
      const lineGeometry = this.lastGeometry.clone();
      const lineFeature = new Feature(lineGeometry.clone());
      lineFeature.setStyle(LINE_STYLE);

      const lineGeoJSON = format.writeFeatureObject(lineFeature, {
        dataProjection: 'EPSG:4326',
        featureProjection: mapProjection,
      });

      const bufferedFeatures = [];
      const inputGeometries = [];

      validDistances.forEach((distM) => {
        const bufferedGeoJSON = turf.buffer(lineGeoJSON, distM, { units: 'meters' });
        if (!bufferedGeoJSON) {
          console.warn(`⚠️ No fue posible generar el buffer de ${distM} m.`);
          return;
        }

        const bufferedFeature = format.readFeature(bufferedGeoJSON, {
          dataProjection: 'EPSG:4326',
          featureProjection: mapProjection,
        });
        const bufferedGeometry = bufferedFeature.getGeometry();

        bufferedFeature.setStyle(BUFFER_STYLE);
        bufferedFeature.set('radius', distM);
        bufferedFeatures.push({ distance: distM, feature: bufferedFeature });
        inputGeometries.push({
          type: bufferedGeometry.getType(),
          coordinates: bufferedGeometry.getCoordinates(),
          radius: distM,
        });
      });

      if (inputGeometries.length === 0) {
        return;
      }

      source.clear();

      // Las áreas mayores se agregan primero para que las menores no queden
      // ocultas. La polilínea se agrega al final y permanece sobre los buffers.
      bufferedFeatures
        .sort((a, b) => b.distance - a.distance)
        .forEach(({ feature }) => source.addFeature(feature));
      source.addFeature(lineFeature);

      // Mantener como referencia la línea, no el último polígono agregado.
      this.lastGeometry = lineGeometry;

      const customGeometry = { input_geometries: inputGeometries };
      this.geoRequestData = customGeometry;
      this.executeMapToolAction("geoprocessing", "ready", {
        geoRequestData: customGeometry,
      });
    },

    /**
     * Dibuja los polígonos de la pluma de dispersión
     * @param {Array} polygons - Geometrías {type, coordinates} en EPSG:3857
     */
    drawPlume(polygons) {
      if (!center) {
        console.warn("⚠️ No hay un punto de origen definido para la pluma.");
        return;
      }

      console.log("✏️ Dibujando pluma:", polygons.length, "polígonos");
      source.clear();

      // 1. Re-agregar el punto de origen
      const pointFeature = new Feature(new Point(center));
      source.addFeature(pointFeature);

      // 2. Agregar los polígonos que devolvió el servicio. El color se asigna
      // por tamaño (el servicio entrega la envolvente primero): la zona más
      // chica es la de mayor concentración, lleva el rojo y se dibuja encima
      // para que ninguna quede tapada cuando son concéntricas
      const features = polygons.map(
        (geom) => new Feature(new Polygon(geom.coordinates))
      );
      features.sort((a, b) => b.getGeometry().getArea() - a.getGeometry().getArea());
      const nZonas = features.length;
      features.forEach((feature, rank) => {
        const zonaIdx = Math.min(nZonas - 1 - rank, PLUME_ZONE_STYLES.length - 1);
        const style = PLUME_ZONE_STYLES[zonaIdx].clone();
        style.setZIndex(rank + 1);
        feature.setStyle(style);
        source.addFeature(feature);
      });

      const customGeometry = {
        input_geometries: polygons,
      };

      this.geoRequestData = customGeometry;

      this.executeMapToolAction("geoprocessing", "ready", {
        geoRequestData: customGeometry,
      });
    },
  },
};
